import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./header";
import Footer from "./Footer";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { userLogin } from "../store/slices/auth";
import { FiEye, FiEyeOff } from "react-icons/fi";

function validate(values) {
    const errors = {};

    if (!values.userId?.trim()) {
        errors.userId = "Please enter your User ID.";
    }

    if (!values.password) {
        errors.password = "Please enter your password.";
    }

    return errors;
}


export default function Login() {

    const [values, setValues] = useState({
        userId: "",
        password: "",
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [note, setNote] = useState(null);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const visualRef = useRef(null);


    useEffect(() => {
        const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        const visual = visualRef.current;

        const blobs = visual
            ? visual.querySelectorAll(":scope > .blob")
            : [];

        if (
            !visual ||
            !blobs.length ||
            reduceMotion ||
            !window.matchMedia("(pointer: fine)").matches
        ) {
            return;
        }

        const onMouseMove = (e) => {
            const rect = visual.getBoundingClientRect();

            const cx =
                (e.clientX - rect.left) / rect.width - 0.5;

            const cy =
                (e.clientY - rect.top) / rect.height - 0.5;

            blobs.forEach((blob, i) => {
                const strength = 18 + i * 6;

                blob.style.translate = `${cx * strength}px ${cy * strength}px`;
            });
        };

        visual.addEventListener("mousemove", onMouseMove, {
            passive: true
        });

        return () =>
            visual.removeEventListener("mousemove", onMouseMove);
    }, []);

    function handleChange(e) {
        const { name, value } = e.target;

        const nextValues = {
            ...values,
            [name]: name === "userId"
                ? value.toUpperCase()
                : value,
        };

        setValues(nextValues);

        const nextErrors = validate(nextValues);

        setErrors((prev) => {
            const updatedErrors = { ...prev };

            if (nextErrors[name]) {
                updatedErrors[name] = nextErrors[name];
            } else {
                delete updatedErrors[name];
            }

            return updatedErrors;
        });
    }
    function handleBlur(e) {
        const { name } = e.target;

        setTouched((prev) => ({
            ...prev,
            [name]: true,
        }));

        const nextErrors = validate(values);

        setErrors((prev) => {
            const updatedErrors = { ...prev };

            if (nextErrors[name]) {
                updatedErrors[name] = nextErrors[name];
            } else {
                delete updatedErrors[name];
            }

            return updatedErrors;
        });
    }

    async function handleSubmit(e) {
        e.preventDefault();

        const nextErrors = validate(values);

        setErrors(nextErrors);

        setTouched({
            userId: true,
            password: true,
        });

        if (Object.keys(nextErrors).length > 0) {
            return;
        }

        try {
            setSubmitting(true);
            setNote(null);

            const payload = {
                userId: values.userId.trim().toUpperCase(),
                password: values.password,
            };

            console.log("Login Payload:", payload);

            const response = await dispatch(
                userLogin(payload)
            ).unwrap();

            console.log(
                "FULL UNWRAPPED RESPONSE:",
                response
            );

            // Check login status
            if (response?.status !== 1) {
                throw new Error(
                    response?.message || "Login failed"
                );
            }

            const userData = response?.data;

            if (!userData) {
                throw new Error(
                    response?.message ||
                    "User data not received"
                );
            }

            const {
                accessToken,
                refreshToken,
                password,
                ...user
            } = userData;

            // Token is required
            if (!accessToken) {
                throw new Error(
                    "Access token not received from server"
                );
            }

            // Save user
            sessionStorage.setItem(
                "user",
                JSON.stringify(user)
            );

            // Save token
            sessionStorage.setItem(
                "accessToken",
                accessToken
            );

            // Save refresh token
            if (refreshToken) {
                sessionStorage.setItem(
                    "refreshToken",
                    refreshToken
                );
            }

            // Login status
            sessionStorage.setItem(
                "isLoggedIn",
                "true"
            );

            // Verify
            console.log(
                "Stored User:",
                sessionStorage.getItem("user")
            );

            console.log(
                "Stored Token:",
                sessionStorage.getItem("accessToken")
            );

            toast.success(
                response?.message || "Login successful"
            );

            navigate("/dashboard", {
                replace: true
            });

        } catch (error) {
            console.error("LOGIN ERROR:", error);

            const apiError =
                error?.response?.data?.message ||
                error?.response?.data?.error?.message ||
                error?.response?.data?.error ||
                error?.message ||
                error?.msg ||
                "Login failed. Please check your credentials.";

            toast.error(apiError);
        } finally {
            setSubmitting(false);
        }
    }


    return (
        <>
            <Header />

            <main className="auth-shell">

                {/* LEFT SIDE */}
                <div className="auth-visual" ref={visualRef}>

                    <div
                        className="blob blob1"
                        aria-hidden="true"
                    ></div>

                    <div
                        className="blob blob2"
                        aria-hidden="true"
                    ></div>

                    <div className="auth-visual-copy">

                        <h2>
                            Welcome back to your{" "}
                            <span className="accent">
                                investment journey.
                            </span>
                        </h2>

                        <p>
                            Access your Global Inavation account and explore
                            opportunities across global markets and investments.
                        </p>

                        <div className="auth-checklist">

                            <div>
                                <span className="chk">✓</span>
                                Explore investment opportunities
                            </div>

                            <div>
                                <span className="chk">✓</span>
                                Secure account access
                            </div>

                            <div>
                                <span className="chk">✓</span>
                                Access global markets
                            </div>

                        </div>

                    </div>

                </div>


                {/* RIGHT SIDE */}
                <div className="auth-form-side">



                    <div className="auth-form-wrap">

                        <h1>Login</h1>

                        <p className="sub">
                            Enter your details to access your account.
                        </p>


                        <form noValidate onSubmit={handleSubmit}>

                            <div
                                className={`field${errors.userId ? " has-error" : ""}`}
                            >
                                <label htmlFor="userId">
                                    User ID
                                </label>

                                <input
                                    type="text"
                                    id="userId"
                                    name="userId"
                                    placeholder="Enter your User ID"
                                    autoComplete="username"
                                    value={values.userId}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    aria-invalid={!!errors.userId}
                                    aria-describedby={
                                        errors.userId
                                            ? "userId-error"
                                            : undefined
                                    }
                                    style={{
                                        textTransform: "uppercase"
                                    }}
                                />

                                {errors.userId && (
                                    <span
                                        className="field-error"
                                        id="userId-error"
                                    >
                                        {errors.userId}
                                    </span>
                                )}
                            </div>
                            <div
                                className={`field${errors.password ? " has-error" : ""
                                    }`}
                            >
                                <label htmlFor="password">
                                    Password
                                </label>

                                <div
                                    style={{
                                        position: "relative",
                                        width: "100%",
                                    }}
                                >
                                    <input
                                        type={
                                            showPassword
                                                ? "text"
                                                : "password"
                                        }
                                        id="password"
                                        name="password"
                                        placeholder="Enter your password"
                                        autoComplete="current-password"
                                        value={values.password}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        aria-invalid={!!errors.password}
                                        aria-describedby={
                                            errors.password
                                                ? "password-error"
                                                : undefined
                                        }
                                        style={{
                                            paddingRight: "50px",
                                        }}
                                    />

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowPassword((prev) => !prev)
                                        }
                                        aria-label={
                                            showPassword
                                                ? "Hide password"
                                                : "Show password"
                                        }
                                        style={{
                                            position: "absolute",
                                            right: "12px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            padding: "4px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "var(--deep)",
                                        }}
                                    >
                                        {showPassword ? (
                                            <FiEyeOff size={20} />
                                        ) : (
                                            <FiEye size={20} />
                                        )}
                                    </button>
                                </div>

                                {errors.password && (
                                    <span
                                        className="field-error"
                                        id="password-error"
                                    >
                                        {errors.password}
                                    </span>
                                )}
                            </div>
                            <button
                                type="submit"
                                className="btn-global btn-primary-global form-submit"
                                disabled={submitting}
                            >
                                {submitting
                                    ? "Logging in…"
                                    : "Login"}
                            </button>


                            {note && (
                                <div
                                    className={`form-note visible ${note.tone}`}
                                >
                                    {note.text}
                                </div>
                            )}

                        </form>
                        <br />
                        <span className="auth-switch mt-5">
                            Don't have an account?{" "}

                            <Link to="/register">
                                Create an account
                            </Link>
                        </span>

                    </div>

                </div>

            </main>

            <Footer />
        </>
    );
}